/* eslint-disable quote-props */
import axios from "axios"
import { jwtDecode } from "jwt-decode"
import { APPLICATION_ID } from "../../utility/constants"
import { getLanguage } from "../../utility/language"
import { installDpop } from "../../core/infrastructure/security/dpop/axios-dpop"
import { getVerificationToken } from "../../core/infrastructure/security/deviceVerification"
import { getOtpPendingToken } from "../../core/infrastructure/security/otpPending"

// FOC-478 — Ruta del endpoint de login tal como la ve el navegador.
// Debe coincidir con el pathname que el gateway lee para la auth pública.
// installDpop lo usa para omitir Authorization y ath en /authentication
// (en el login todavía no hay token que firmar).
const LOGIN_PATH = "/api/user/authentication"

// Peticiones públicas: no se les manda token ni se les hace refresh.
// El installDpop ya exceptúa el login por LOGIN_PATH, pero mantenemos la
// lista para que el refresh preventivo las ignore también.
const PUBLIC_ENDPOINTS = [
  "user/authentication",
  "user/password/forgot",
  "user/password/verifyCode",
  "user/password/restore"
]

// FOC-478 — Lee el token de sesión guardado en localStorage.
// FOC-492 — Si no hay token de sesión, cae al token de verificación de
// dispositivo (purpose=device_verification) para que /device/verify y
// /device/resend se firmen con la misma instancia axios.
// FOC-491 AC1 — También cae al token de OTP pending (purpose=otp_pending)
// para que /otp/validate se firme desde la misma instancia.
// Orden de fallback: session → otp_pending → device_verification. El flujo
// es /authentication → /otp/validate → (si new device) /device/verify, por
// eso otp_pending se consulta primero: en /otp/validate ya no hay sesión y
// device_verification aún no fue emitido.
const getUserToken = () => {
  const raw = localStorage.getItem("tokenBP")
  if (raw && raw !== "undefined") return raw
  if (raw === "undefined") localStorage.removeItem("tokenBP")

  return getOtpPendingToken() ?? getVerificationToken()
}

// FOC-488 — Al llegar un 401 con código de sesión (TOKEN_INVALID,
// DPOP_PROOF_INVALID, DEVICE_MISMATCH, APPLICATION_MISMATCH, SESSION_REVOKED)
// limpiamos residuos y navegamos a /login con el motivo en query. La pantalla
// de login muestra el toast correspondiente y limpia el parámetro.
const onSessionError = (code) => {
  if (typeof window === "undefined") return
  localStorage.removeItem("tokenBP")
  localStorage.removeItem("userInfo")
  localStorage.removeItem("userInfoTFA")
  localStorage.removeItem("userEmployee")

  // Navegación completa: fuerza recarga del bundle más reciente del front.
  window.location.assign(`/login?motivo=${code}`)
}

export const API = (data) => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      "Accept-Language": getLanguage(),
      "application-id": APPLICATION_ID
    }
  })

  // FOC-478 — DPoP se registra PRIMERO para correr al final: cuando el
  // refresh preventivo (de abajo) ya actualizó tokenBP, el firmante lee
  // el token nuevo con getUserToken().
  installDpop(instance, {
    loginPath: LOGIN_PATH,
    getToken: getUserToken,
    onSessionError
  })

  // Refresh preventivo: si el JWT expiró, pide uno nuevo antes de que la
  // request real salga. Registrado DESPUÉS de installDpop → corre PRIMERO.
  // El flag config.__isRefresh evita el ciclo cuando el propio refresh
  // dispara el interceptor otra vez.
  instance.interceptors.request.use(
    async (config) => {
      if (config.__isRefresh) return config
      if (PUBLIC_ENDPOINTS.includes(config.url)) return config

      const token = localStorage.getItem("tokenBP")
      if (!token) return config

      let decoded
      try {
        decoded = jwtDecode(token)
      } catch {
        return config
      }

      const nowSec = Math.floor(Date.now() / 1000)
      if (decoded?.exp && decoded.exp < nowSec) {
        try {
          const refreshRes = await instance.get("user/token/refresh", {
            __isRefresh: true
          })
          const newToken = refreshRes?.data?.data?.newToken
          if (newToken) {
            localStorage.setItem("tokenBP", newToken)
            if (data?.setToken) data.setToken(newToken)
          }
        } catch {
          // Si el refresh falla con 401, el response interceptor de
          // installDpop dispara onSessionError y navega al login.
          // Dejamos que la request original siga: también fallará y
          // disparará la misma lógica.
        }
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  return instance
}

// FOC-477 — Instancia que firma con el token temporal (scope "password-change").
// Se usa durante el flujo de cambio forzado tras un 403 PASSWORD_EXPIRED del
// login. El tempToken se guarda en sessionStorage, no en localStorage, y vence
// a los 15 minutos. El request interceptor de installDpop firma la prueba con
// ese token como ath y lo manda en Authorization.
export const API_TEMP = () => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      "Accept-Language": getLanguage(),
      "application-id": APPLICATION_ID
    },
    validateStatus: () => true
  })

  // El flujo temporal no quiere la recarga a /login: el controlador decide
  // qué hacer con el error (p. ej. mostrar "token vencido" en la misma
  // pantalla de cambio obligatorio).
  installDpop(instance, {
    loginPath: LOGIN_PATH,
    getToken: () => (typeof window !== "undefined" ? sessionStorage.getItem("tempToken") : null),
    onSessionError: () => undefined
  })

  return instance
}
